import { defineComponent, ref, h } from 'vue';
import { QIcon, QInput, QSelect, QBtn, QMenu, QList, QItem, QItemSection, QItemLabel, QTooltip, QCheckbox } from 'quasar';
import utils from '../../../../../Shared/utils.jsx';
import webSocket from '@/Services/webSocketService';

import { AgGridVue } from "ag-grid-vue3";

import GenericDialog from '../shared/genericDialog.jsx';
import FolderMenu from './folderMenu.vue';
import SchemaForm from '../../../schemaform/SchemaForm.jsx';
import { projectTreeIcon, projectTreeTooltip, CHANNEL_TYPE_CALL, loadFlowChannelList, builtinChannelFallbackList, isSiblingMethodDraggable, FLOWV2_FOLDER_PROJECT_MIME, canMoveToTarget } from '../debugger/flowChannelConfig.js';
import { resolveLogicalUri } from '../debugger/flowV2WebUrl.js';
import { resolveGroupAccent } from '../shared/helpers.js';
import { resolveCanvasSourceHighlight } from './folderGridCanvasHighlight.js';
import { itemFullPath, newPathForTarget, repathSubtree, destinationNameTaken } from './folderMove.js';
import FlowV2ListToolbar from './flowv2ListToolbar.jsx';
import './folderGrid.css';

/** Shared with FolderLabel (ag-grid inner renderer has limited params wiring). */
let _folderChannelList = [];
/** Open flow's channel — used to disable incompatible sibling drags. */
let _folderCallerChannel = null;
/** Open method type — APIHeader may drag any channel; FlowHeader stays canvas-strict. */
let _folderItemType = null;
/** Open project — used so the current project is not a sibling drag source. */
let _folderCurrentProjectId = null;
/** Project ids locked by a collaborative (or local) debug session. */
let _folderLockedProjectIds = [];

/** In-progress folder-tree drag (same window). Canvas and Imports still read dataTransfer. */
let _activeTreeDrag = null;

function ancestorProjectNode(node) {
    let walk = node;
    while (walk?.data && walk.data.Type != 'Project' && walk.parent)
        walk = walk.parent;
    return walk?.data?.Type == 'Project' ? walk : null;
}

function ancestorProjectChannelType(node) {
    const project = ancestorProjectNode(node);
    return project?.data?.ChannelType || null;
}

function ancestorProjectId(node) {
    const project = ancestorProjectNode(node);
    return project?.data?.ID || null;
}

function isFolderRowDebugLocked(data, node) {
    if (!_folderLockedProjectIds?.length || !data)
        return false;
    const projectId = data.Type == 'Project' ? data.ID : ancestorProjectId(node);
    if (!projectId)
        return false;
    return _folderLockedProjectIds.some(id => id == projectId);
}

const FolderLabel = defineComponent({
    name: 'flow-v2-folder-label',
    components: { QIcon, QTooltip },
    data: function () {
        return {}
    },
    methods: {
        handleDragStart(ev, data) {
            const channelType = ancestorProjectChannelType(this.params.node);
            let walk = this.params.node;
            let project = null;
            let cls = null;
            while (walk?.data) {
                if (walk.data.Type == 'Class' && !cls)
                    cls = walk.data;
                if (walk.data.Type == 'Project') {
                    project = walk.data;
                    break;
                }
                walk = walk.parent;
            }
            const projectName = project?.Name || '';
            const className = cls?.Name || (projectName ? `Default${projectName}` : '');
            const payload = {
                origin: 'FlowV2FolderTree',
                dragKind: 'folder-tree',
                ...data,
                ChannelType: channelType || data.ChannelType || null,
                ProjectID: project?.ID || (data.Type == 'Project' ? data.ID : null),
                ProjectName: projectName,
                ClassName: className,
                ClassID: cls?.ID || project?.ID || null,
                ClassFullName: projectName && className ? `User.${projectName}.${className}` : '',
            };
            _activeTreeDrag = payload;
            ev.dataTransfer.effectAllowed = 'copyMove';
            if (data.Type == 'Project')
                ev.dataTransfer.setData(FLOWV2_FOLDER_PROJECT_MIME, data.ID || '1');
            ev.dataTransfer.setData('application/json', JSON.stringify(payload));
            ev.dataTransfer.setDragImage(ev.target, 10, 10);
        },
        handleDragEnd() {
            _activeTreeDrag = null;
        }
    },
    render() {
        let icon;
        let tip = null;
        const data = this.params.data;
        if (!data)
            return <span class="flowv2-folder-label">{this.params.value || ''}</span>;
        const isMethod = data.Type == 'FlowHeader' || data.Type == 'APIHeader';
        const isProject = data.Type == 'Project';
        const isFolder = data.Type == 'Folder';
        const isClass = data.Type == 'Class';
        const rowLocked = isFolderRowDebugLocked(data, this.params.node);
        const calleeChannelType = isMethod ? ancestorProjectChannelType(this.params.node) : (isProject ? (data.ChannelType || null) : null);
        const channelOk = isSiblingMethodDraggable(_folderCallerChannel, calleeChannelType, _folderChannelList, _folderItemType);
        const isSelfProject = isProject && data.ID && data.ID == _folderCurrentProjectId;
        const canDrag = !rowLocked && (isFolder || isProject || isClass || isMethod);
        switch (data.Type) {
            case 'Folder': icon = 'mdi-folder'; break;
            case 'Project':
                icon = projectTreeIcon(data.ChannelType, _folderChannelList);
                tip = projectTreeTooltip(data.ChannelType, _folderChannelList);
                break;
            case 'Class': icon = 'mdi-window-maximize'; break;
            case 'FlowHeader': icon = 'mdi-sitemap-outline'; break;
            case 'APIHeader': icon = 'mdi-code-braces-box'; break;
        }
        if (rowLocked)
            tip = 'Read-only — debugging in progress';
        else if (isFolder)
            tip = 'Drag onto a folder to move.';
        else if (isClass)
            tip = 'Drag onto a same-channel project to move.';
        else if (isSelfProject)
            tip = 'Drag to a folder to move this project.';
        else if (isProject)
            tip = 'Drag to a folder to move, or onto Imports to add as a sibling.';
        else if (isMethod && channelOk)
            tip = 'Drag onto a class or project to move, or onto the flowchart to add a sibling.';
        else if (isMethod)
            tip = 'Drag onto a same-channel class or project to move.';
        const accent = isMethod ? resolveGroupAccent(null) : null;
        const iconEl = isMethod
            ? (
                <div
                    class="flowv2-folder-icon-well"
                    style={{ backgroundColor: accent.well, color: accent.glyph }}
                >
                    <QIcon name={icon} size="16px" />
                </div>
            )
            : <QIcon name={icon} size="20px" class="q-mr-sm" />;
        const lockEl = rowLocked
            ? (
                <QIcon name="mdi-lock-outline" size="14px" color="blue-grey-5" class="q-ml-xs">
                    <QTooltip>Read-only — debugging in progress</QTooltip>
                </QIcon>
            )
            : null;
        return (
            <span
                class="flowv2-folder-label"
                title={tip || undefined}
                draggable={canDrag}
                style={canDrag ? { cursor: 'grab' } : undefined}
                onDragstart={(e) => this.handleDragStart(e, data)}
                onDragend={this.handleDragEnd}
            >{iconEl}{this.params.value}{lockEl}</span>
        );
    }
});

export default defineComponent({
    name: 'flowv2-folder-grid',
    components: {
        QIcon, QInput, QSelect, QBtn, QMenu, QList, QItem, QItemSection, QItemLabel, QTooltip,
        FlowV2ListToolbar,
        AgGridVue,
        GenericDialog,
        FolderMenu,
        FolderLabel,
        SchemaForm,
    },
    emits: ['open-flow', 'update-method', 'rename-method', 'open-config', 'moved-method', 'moved-project', 'save-recipe'],
    data: function () {
        return {
            folderSearchText: '',
            toolFilter: '',
            folderData: [],
            folderColumnDefs: [
                {
                    field: 'Menu',
                    minWidth: 50,
                    maxWidth: 70,
                    cellRenderer: 'FolderMenu',
                    cellRendererParams: {
                        emit: this.folderEvent,
                        lockedProjectIds: [],
                        askAiEnabled: false,
                    }
                }
            ],
            gridComponents: {
                FolderLabel,
                FolderMenu, // Map the component to its name for AG Grid
            },
            defaultColDef: {
                //flex: 1,
            },
            autoGroupColumnDef: {
                colId: 'ID',
                field: 'Name',
                headerName: 'File Explorer',
                cellRenderer: 'agGroupCellRenderer',
                rowDrag: false,
                flex: 1,
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: 'FolderLabel'
                },
            },
            gridApi: null,

            inputDialogTitle: null,
            inputDialogType: null,
            inputDialogSaveFunc: null,
            inputDialogOpen: false,
            inputDialogValue: '',
            inputDialogBody: '',
            channelList: [],
            newProjectName: '',
            newProjectChannelType: CHANNEL_TYPE_CALL,
            newMethodName: '',
            startFromExample: false,
            exampleRecipes: [],
            exampleRecipeId: '',
            exampleRecipeFilter: '',
            exampleRecipesLoading: false,
            exampleRecipesEmpty: false,
            canvasSourceId: null,
            canvasHighlightId: null,
            canvasHighlightRole: null,
            dropTargetId: null,
        }
    },
    props: {
        FlowID: null,
        /** 'document' | 'config' — from FlowV2 editor surface */
        editorSurface: { type: String, default: 'document' },
        /** 'method' | 'project' — meaningful when editorSurface is config */
        configScope: { type: String, default: 'method' },
        /** Active project id (parent of the open method), for project-config row highlight */
        projectId: { type: String, default: null },
        /** Open flow channel — disables drag of conflicting sibling methods */
        channelConfig: { type: Object, default: null },
        /** FlowHeader | APIHeader — API editors may drag any channel method */
        itemType: { type: String, default: null },
        /** @deprecated Prefer lockedProjectIds — kept so callers can pass editorReadOnly without hiding menus. */
        readOnly: { type: Boolean, default: false },
        lockedProjectIds: { type: Array, default: () => [] },
        /** Ask AI is on, so flowchart and API rows can save a recipe. */
        askAiEnabled: { type: Boolean, default: false },
    },
    async created() {
        try {
            this.channelList = await loadFlowChannelList((url) => utils.api.get(url));
        }
        catch (e) {
            this.channelList = builtinChannelFallbackList();
        }
        _folderChannelList = this.channelList;
        _folderCallerChannel = this.channelConfig;
        _folderItemType = this.itemType;
        _folderCurrentProjectId = this.projectId;
        _folderLockedProjectIds = this.lockedProjectIds || [];
        this.folderData = await utils.api.get('Apps/FlowV2/ListAllFolderItems');
        this.beginFolderMonitor();
        this.$nextTick(() => this.scheduleRevealActiveFlow());
    },
    unmounted() {
        this.endFolderMonitor();
        if (this._onWindowDragEnd)
            window.removeEventListener('dragend', this._onWindowDragEnd);
    },
    mounted() {
        this._onWindowDragEnd = () => this.clearTreeDrop();
        window.addEventListener('dragend', this._onWindowDragEnd);
    },
    watch: {
        FlowID() {
            if (this.canvasSourceId && this.canvasSourceId === this.FlowID)
                this.clearCanvasSourceHighlight();
            this.scheduleRowStyleRedraw();
            this.scheduleRevealActiveFlow();
        },
        editorSurface() {
            this.scheduleRowStyleRedraw();
        },
        configScope() {
            this.scheduleRowStyleRedraw();
        },
        projectId: {
            immediate: true,
            handler(id) {
                _folderCurrentProjectId = id;
                this.scheduleRowStyleRedraw();
            },
        },
        lockedProjectIds: {
            immediate: true,
            deep: true,
            handler(ids) {
                _folderLockedProjectIds = ids || [];
                this.syncFolderMenuParams();
                this.scheduleRowStyleRedraw();
            },
        },
        askAiEnabled: {
            immediate: true,
            handler() {
                this.syncFolderMenuParams();
            },
        },
        channelConfig: {
            immediate: true,
            handler(cfg) {
                _folderCallerChannel = cfg;
                this.scheduleRowStyleRedraw();
            },
        },
        itemType: {
            immediate: true,
            handler(type) {
                _folderItemType = type;
                this.scheduleRowStyleRedraw();
            },
        },
    },
    methods: {
        /**
         * getRowStyle depends on FlowID / project config props. Never call redrawRows
         * synchronously from a Vue watcher — AG Grid may still be mid-render (error #252).
         */
        syncFolderMenuParams() {
            const col = this.folderColumnDefs[0];
            if (!col)
                return;
            this.folderColumnDefs = [{
                ...col,
                cellRendererParams: {
                    emit: this.folderEvent,
                    lockedProjectIds: _folderLockedProjectIds,
                    askAiEnabled: !!this.askAiEnabled,
                },
            }];
        },
        scheduleRowStyleRedraw(afterRedraw) {
            if (afterRedraw)
                this._afterRowStyleRedraw = afterRedraw;
            if (this._rowStyleRedrawScheduled)
                return;
            this._rowStyleRedrawScheduled = true;
            this.$nextTick(() => {
                setTimeout(() => {
                    this._rowStyleRedrawScheduled = false;
                    this.gridApi?.redrawRows();
                    const after = this._afterRowStyleRedraw;
                    this._afterRowStyleRedraw = null;
                    if (after)
                        this.$nextTick(() => after());
                }, 0);
            });
        },

        gridReady(params) {
            this.gridApi = params.api;
            const pending = this._pendingRevealId || this.FlowID;
            if (pending)
                this.revealRowInGrid(pending);
            if (this._pendingCanvasSourceId)
                this.highlightCanvasSource(this._pendingCanvasSourceId);
        },

        onFirstDataRendered() {
            const pending = this._pendingRevealId || this.FlowID;
            if (pending)
                this.revealRowInGrid(pending);
            if (this._pendingCanvasSourceId)
                this.highlightCanvasSource(this._pendingCanvasSourceId);
        },

        /**
         * Cue the folder row that matches a selected sibling-method canvas icon.
         * Does not expand or scroll. Hover Identify uses revealCanvasSource
         * (revealRowInGrid then this pulse). Persistent wash stays on the child
         * method row. If that row is collapsed, the visible parent pulses once
         * and does not keep the highlight.
         */
        highlightCanvasSource(toolId) {
            this.clearEphemeralAncestorPulse();
            this._pendingCanvasSourceId = null;
            if (!toolId || toolId === this.FlowID) {
                this.clearCanvasSourceHighlight();
                return;
            }
            if (!this.gridApi) {
                this._pendingCanvasSourceId = toolId;
                this.canvasSourceId = toolId;
                return;
            }

            const sameSource = this.canvasSourceId === toolId;
            this.canvasSourceId = toolId;
            this.canvasHighlightId = toolId;

            const resolved = resolveCanvasSourceHighlight(this.gridApi, toolId);
            if (!resolved) {
                this.canvasHighlightRole = null;
                this.scheduleRowStyleRedraw();
                return;
            }

            this.canvasHighlightRole = resolved.role;
            if (resolved.role === 'ancestor') {
                if (sameSource)
                    this.playCanvasSourcePulse(resolved.id, { ephemeral: true });
                else
                    this.scheduleRowStyleRedraw(() => this.playCanvasSourcePulse(resolved.id, { ephemeral: true }));
                return;
            }

            if (sameSource && resolved.role === 'source')
                this.playCanvasSourcePulse(resolved.id);
            else
                this.scheduleRowStyleRedraw(() => this.playCanvasSourcePulse(resolved.id));
        },

        clearCanvasSourceHighlight() {
            this._pendingCanvasSourceId = null;
            this.clearEphemeralAncestorPulse();
            if (!this.canvasSourceId && !this.canvasHighlightId)
                return;
            this.canvasSourceId = null;
            this.canvasHighlightId = null;
            this.canvasHighlightRole = null;
            this.scheduleRowStyleRedraw();
        },

        onFolderRowGroupOpened() {
            if (!this.canvasSourceId || !this.gridApi)
                return;
            const resolved = resolveCanvasSourceHighlight(this.gridApi, this.canvasSourceId);
            if (resolved?.role !== 'source')
                return;
            this.canvasHighlightId = resolved.id;
            this.canvasHighlightRole = 'source';
            this.clearEphemeralAncestorPulse();
            this.scheduleRowStyleRedraw();
        },

        findFolderRowElement(rowId) {
            if (!rowId)
                return null;
            const node = this.gridApi?.getRowNode?.(rowId);
            if (node?.eRow)
                return node.eRow;
            const escaped = typeof CSS !== 'undefined' && CSS.escape
                ? CSS.escape(rowId)
                : String(rowId).replace(/["\\]/g, '\\$&');
            const roots = [
                this.$refs.folderAgGrid?.$el,
                typeof this.gridApi?.getGui === 'function' ? this.gridApi.getGui() : null,
            ];
            for (const root of roots) {
                const el = root?.querySelector?.(`[row-id="${escaped}"]`);
                if (el)
                    return el;
            }
            return null;
        },

        playCanvasSourcePulse(rowId, { ephemeral } = {}) {
            const el = this.findFolderRowElement(rowId);
            if (!el)
                return;
            if (ephemeral)
                el.classList.add('flowv2-folder-row--canvas-source');
            el.classList.remove('flowv2-folder-row--canvas-pulse');
            void el.offsetWidth;
            el.classList.add('flowv2-folder-row--canvas-pulse');
            if (!ephemeral)
                return;
            this._ephemeralPulseEl = el;
            this._ephemeralPulseTimer = setTimeout(() => {
                this._ephemeralPulseTimer = null;
                this.clearEphemeralAncestorPulse();
            }, 1150);
        },

        clearEphemeralAncestorPulse() {
            if (this._ephemeralPulseTimer) {
                clearTimeout(this._ephemeralPulseTimer);
                this._ephemeralPulseTimer = null;
            }
            const el = this._ephemeralPulseEl;
            this._ephemeralPulseEl = null;
            if (!el)
                return;
            el.classList.remove('flowv2-folder-row--canvas-pulse');
            const rowId = el.getAttribute?.('row-id');
            if (rowId !== this.canvasSourceId)
                el.classList.remove('flowv2-folder-row--canvas-source');
        },

        getRowClass(params) {
            const rowId = params.node?.data?.ID;
            if (!rowId || !this.canvasSourceId || rowId !== this.canvasSourceId)
                return undefined;
            if (rowId === this.FlowID)
                return undefined;
            return 'flowv2-folder-row--canvas-source';
        },

        scheduleRevealActiveFlow() {
            if (this.FlowID)
                this.revealRowInGrid(this.FlowID);
        },

        /** Expand the tree row under which a child was added (context menu row). */
        expandParentRowAfterAdd(parentData) {
            if (!this.gridApi || !parentData?.ID) return;
            const parentNode = this.gridApi.getRowNode(parentData.ID);
            if (parentNode && !parentNode.expanded)
                parentNode.setExpanded(true);
        },

        /** Expand/scroll the method row, then apply the canvas-source highlight + pulse. */
        revealCanvasSource(toolId) {
            if (!toolId)
                return;
            this.revealRowInGrid(toolId);
            this.$nextTick(() => this.highlightCanvasSource(toolId));
        },

        /**
         * Same as clicking the method in the folder tree.
         * @returns {boolean} false when the row is not in the grid (caller may fall back).
         */
        openFlowById(toolId) {
            if (!toolId || !this.gridApi)
                return false;
            const node = this.gridApi.getRowNode(toolId);
            if (!node?.data)
                return false;
            this.emitOpenFlowFromRow(node.data, node);
            return true;
        },

        /** Same as clicking a FlowHeader / APIHeader: open in editor and highlight row. */
        emitOpenFlowFromRow(rowData, node, isNew, recipeId) {
            if (!rowData?.ID) return;
            let walk = node ?? this.gridApi?.getRowNode(rowData.ID);
            if (!walk) return;
            while (walk.data && walk.data.Type != 'Project' && walk.parent)
                walk = walk.parent;
            const parentID = walk && walk.data && walk.data.Type == 'Project' ? walk.data.ID : undefined;
            this.$emit('open-flow', rowData.ID, rowData.Type, rowData.Name, parentID, !!isNew, recipeId || '');
        },

        revealRowInGrid(rowId) {
            if (!rowId)
                return;
            if (!this.gridApi) {
                this._pendingRevealId = rowId;
                return;
            }
            const node = this.gridApi.getRowNode(rowId);
            if (!node) {
                this._pendingRevealId = rowId;
                return;
            }
            this._pendingRevealId = null;
            let walk = node.parent;
            while (walk) {
                if (walk.data && !walk.expanded && typeof walk.setExpanded === 'function')
                    walk.setExpanded(true);
                walk = walk.parent;
            }
            this.$nextTick(() => {
                const visible = this.gridApi?.getRowNode(rowId);
                if (visible)
                    this.gridApi.ensureNodeVisible(visible, 'middle');
            });
        },

        /**
         * Rename a FlowHeader/APIHeader row. Tree getDataPath includes Name, so
         * hierarchy-changing updates must remove+add (AG Grid tree-data rule).
         */
        updateMethodName(id, name) {
            if (!this.gridApi || !id || name == null || name === '')
                return;
            const node = this.gridApi.getRowNode(id);
            if (!node?.data || node.data.Name === name)
                return;
            const updated = { ...node.data, Name: name };
            this.gridApi.applyTransaction({ remove: [node.data] });
            this.gridApi.applyTransaction({ add: [updated] });
            const idx = this.folderData.findIndex((r) => r.ID === id);
            if (idx >= 0)
                this.folderData.splice(idx, 1, updated);
        },

        clearTreeDrop() {
            this.clearExpandTimer();
            if (this._dropHighlightEl) {
                this._dropHighlightEl.classList.remove('flowv2-folder-row--drop-ok');
                this._dropHighlightEl = null;
            }
            this.dropTargetId = null;
            _activeTreeDrag = null;
            this.hideDropHint();
        },
        showDropHint(reason, ev) {
            const el = this.$refs.dropHint;
            if (!el)
                return;
            if (!reason) {
                el.style.visibility = 'hidden';
                return;
            }
            if (el.textContent !== reason)
                el.textContent = reason;
            const width = 260;
            const left = Math.max(8, Math.min(ev.clientX + 14, window.innerWidth - width - 8));
            const top = Math.max(8, Math.min(ev.clientY + 18, window.innerHeight - 40));
            el.style.left = `${left}px`;
            el.style.top = `${top}px`;
            el.style.visibility = 'visible';
        },
        hideDropHint() {
            const el = this.$refs.dropHint;
            if (el)
                el.style.visibility = 'hidden';
        },
        clearExpandTimer() {
            if (this._expandTimer) {
                clearTimeout(this._expandTimer);
                this._expandTimer = null;
            }
            this._expandNodeId = null;
        },
        rowFromDragEvent(ev) {
            const rowEl = ev.target?.closest?.('.ag-row');
            const rowId = rowEl?.getAttribute?.('row-id');
            if (!rowId || !this.gridApi)
                return null;
            return this.gridApi.getRowNode(rowId) || null;
        },
        projectForRow(data, node) {
            if (!data)
                return null;
            if (data.Type === 'Project')
                return data;
            const projectNode = ancestorProjectNode(node);
            return projectNode?.data || null;
        },
        rememberFolderRow(row) {
            if (!row?.ID)
                return;
            const idx = this.folderData.findIndex(r => r.ID === row.ID);
            if (idx >= 0)
                this.folderData.splice(idx, 1, { ...this.folderData[idx], ...row });
            else
                this.folderData.push({ ...row });
        },
        forgetFolderRows(rows) {
            const ids = new Set((rows || []).map(r => r?.ID).filter(Boolean));
            if (!ids.size)
                return;
            for (let i = this.folderData.length - 1; i >= 0; i--) {
                if (ids.has(this.folderData[i].ID))
                    this.folderData.splice(i, 1);
            }
        },
        // Creates and live Add* events update the grid with a transaction and do not
        // always land in folderData. A later rowData refresh then drops those rows
        // and leaves a blank tree group where their children moved.
        adoptGridRows() {
            if (!this.gridApi?.forEachNode)
                return;
            this.gridApi.forEachNode(node => {
                if (node?.data?.ID && !this.folderData.some(r => r.ID === node.data.ID))
                    this.folderData.push({ ...node.data });
            });
        },
        evaluateMove(dragged, target) {
            this.adoptGridRows();
            if (!dragged)
                return { ok: false, reason: 'Nothing to move' };
            const sourceNode = this.gridApi?.getRowNode?.(dragged.ID);
            if (sourceNode && isFolderRowDebugLocked(sourceNode.data, sourceNode))
                return { ok: false, reason: 'Read-only — debugging in progress' };
            const targetNode = target?.ID ? this.gridApi?.getRowNode?.(target.ID) : null;
            if (target && target.ID && !targetNode)
                return { ok: false, reason: 'That item is no longer in the tree' };
            if (targetNode && isFolderRowDebugLocked(targetNode.data, targetNode))
                return { ok: false, reason: 'Read-only — debugging in progress' };
            const sourceProject = this.projectForRow(sourceNode?.data || dragged, sourceNode);
            const destProject = targetNode ? this.projectForRow(targetNode.data, targetNode) : null;
            const decision = canMoveToTarget(dragged, target, {
                sourceChannelType: sourceProject?.ChannelType,
                destChannelType: destProject?.ChannelType,
                sourceFullPath: itemFullPath(dragged),
                destFullPath: target ? itemFullPath(target) : '',
            });
            if (!decision.ok)
                return decision;
            const newPath = newPathForTarget(target);
            const exclude = new Set((repathSubtree(this.folderData, dragged, newPath) || []).map(u => u.oldId));
            exclude.add(dragged.ID);
            if (destinationNameTaken(this.folderData, dragged.Name, newPath, exclude))
                return { ok: false, reason: `An item named '${dragged.Name}' already exists there` };
            return { ok: true, reason: null, newPath };
        },
        setDropHighlight(rowId) {
            if (this._dropHighlightEl) {
                this._dropHighlightEl.classList.remove('flowv2-folder-row--drop-ok');
                this._dropHighlightEl = null;
            }
            this.dropTargetId = rowId || null;
            if (!rowId || rowId === '__root__' || !this.$el)
                return;
            const escaped = window.CSS?.escape ? window.CSS.escape(rowId) : rowId.replace(/"/g, '\\"');
            const el = this.$el.querySelector(`.ag-row[row-id="${escaped}"]`);
            if (!el)
                return;
            el.classList.add('flowv2-folder-row--drop-ok');
            this._dropHighlightEl = el;
        },
        scheduleExpand(node) {
            if (!node?.data?.ID || node.expanded)
                return;
            if (this._expandNodeId === node.data.ID)
                return;
            this.clearExpandTimer();
            this._expandNodeId = node.data.ID;
            this._expandTimer = setTimeout(() => {
                if (typeof node.setExpanded === 'function')
                    node.setExpanded(true);
            }, 500);
        },
        onTreeDragOver(ev) {
            if (!_activeTreeDrag)
                return;
            const inGrid = !!ev.target?.closest?.('.ag-root-wrapper, .ag-body-viewport, .ag-center-cols-container');
            if (!inGrid)
                return;
            ev.preventDefault();
            const node = this.rowFromDragEvent(ev);
            const target = node?.data || null;
            const decision = this.evaluateMove(_activeTreeDrag, node ? target : null);
            if (ev.dataTransfer)
                ev.dataTransfer.dropEffect = decision.ok ? 'move' : 'none';
            this.showDropHint(decision.ok ? '' : decision.reason, ev);
            if (node && (node.data.Type === 'Folder' || node.data.Type === 'Project' || node.data.Type === 'Class'))
                this.scheduleExpand(node);
            else
                this.clearExpandTimer();
            const highlight = !decision.ok ? null : (node ? target.ID : '__root__');
            if (highlight !== this.dropTargetId || !highlight)
                this.setDropHighlight(highlight);
        },
        onRootDragOver(ev) {
            if (!_activeTreeDrag)
                return;
            ev.preventDefault();
            ev.stopPropagation();
            const decision = this.evaluateMove(_activeTreeDrag, null);
            if (ev.dataTransfer)
                ev.dataTransfer.dropEffect = decision.ok ? 'move' : 'none';
            this.showDropHint(decision.ok ? '' : decision.reason, ev);
            this.clearExpandTimer();
            this.setDropHighlight(decision.ok ? '__root__' : null);
        },
        onTreeDrop(ev) {
            if (!_activeTreeDrag)
                return;
            ev.preventDefault();
            const dragged = _activeTreeDrag;
            const inGrid = !!ev.target?.closest?.('.ag-root-wrapper, .ag-body-viewport, .ag-center-cols-container');
            const node = this.rowFromDragEvent(ev);
            this.clearTreeDrop();
            if (!inGrid)
                return;
            this.performMove(dragged, node?.data || null);
        },
        onRootDrop(ev) {
            if (!_activeTreeDrag)
                return;
            ev.preventDefault();
            ev.stopPropagation();
            const dragged = _activeTreeDrag;
            this.clearTreeDrop();
            this.performMove(dragged, null);
        },
        applyMovedRows(updates) {
            if (!this.gridApi || !updates?.length)
                return;
            const remove = [];
            const add = [];
            for (const update of updates) {
                const previous = this.folderData.find(row => row.ID === update.oldId)
                    || this.folderData.find(row => row.ID === update.row.ID)
                    || {};
                const next = { ...previous, ...update.row };
                update.row = next;
                const existing = this.gridApi.getRowNode(next.ID);
                const already = existing?.data
                    && (existing.data.Path || '') === (next.Path || '')
                    && existing.data.ID === next.ID
                    && existing.data.Name === next.Name;
                if (already)
                    continue;
                if (update.oldId) {
                    const oldNode = this.gridApi.getRowNode(update.oldId);
                    if (oldNode?.data)
                        remove.push(oldNode.data);
                }
                if (existing?.data && existing.data.ID !== update.oldId)
                    remove.push(existing.data);
                add.push(next);
            }
            if (remove.length)
                this.gridApi.applyTransaction({ remove });
            if (add.length)
                this.gridApi.applyTransaction({ add });
            for (const update of updates) {
                let idx = this.folderData.findIndex(row => row.ID === update.oldId);
                if (idx < 0)
                    idx = this.folderData.findIndex(row => row.ID === update.row.ID);
                if (idx >= 0)
                    this.folderData.splice(idx, 1, update.row);
                else
                    this.folderData.push(update.row);
            }
        },
        restoreFolderSnapshot(snapshot) {
            this.folderData = snapshot.map(row => ({ ...row }));
            this.gridApi?.setGridOption?.('rowData', this.folderData);
        },
        notifyMoved(updates) {
            const openId = this.FlowID;
            const openProject = this.projectId;
            for (const update of updates) {
                const row = update.row;
                if (openId && row.ID === openId && (row.Type === 'FlowHeader' || row.Type === 'APIHeader')) {
                    const node = this.gridApi?.getRowNode?.(row.ID);
                    const project = this.projectForRow(row, node);
                    this.$emit('moved-method', {
                        id: row.ID,
                        type: row.Type,
                        projectId: project?.ID || null,
                    });
                }
                if (openProject && row.ID === openProject && row.Type === 'Project')
                    this.$emit('moved-project', { id: row.ID });
            }
        },
        async performMove(dragged, target) {
            const decision = this.evaluateMove(dragged, target);
            if (!decision.ok)
                return;
            const updates = repathSubtree(this.folderData, dragged, decision.newPath);
            if (!updates.length)
                return;
            const snapshot = this.folderData.map(row => ({ ...row }));
            this.applyMovedRows(updates);
            const endpoint = dragged.Type === 'Folder'
                ? 'Apps/FlowV2/MoveFolder'
                : dragged.Type === 'Project'
                    ? 'Apps/FlowV2/MoveProject'
                    : dragged.Type === 'Class'
                        ? 'Apps/FlowV2/MoveClass'
                        : 'Apps/FlowV2/MoveMethod';
            const body = dragged.Type === 'Folder'
                ? { FullName: dragged.ID, NewPath: decision.newPath }
                : { ID: dragged.ID, Type: dragged.Type, NewPath: decision.newPath };
            try {
                const res = await utils.api.request('POST', endpoint, body);
                if (!res?.Success) {
                    this.restoreFolderSnapshot(snapshot);
                    alert(res?.Reason || 'Move failed');
                    return;
                }
                if (Array.isArray(res.Rows) && res.Rows.length)
                    this.applyMovedRows(res.Rows.map(row => ({ oldId: row.OldID || row.ID, row })));
                this.notifyMoved(updates);
                const moved = updates.find(update => update.oldId === dragged.ID);
                if (moved?.row?.ID)
                    this.revealRowInGrid(moved.row.ID);
            }
            catch (err) {
                this.restoreFolderSnapshot(snapshot);
                alert(err?.message || 'Move failed');
            }
        },

        beginFolderMonitor() {
            console.log(`FlowV2 beginFolderMonitor`);
            webSocket.StartFolderBrowser(this.eventCallback);
        },
        endFolderMonitor() {
            console.log(`FlowV2 endFolderMonitor`);
            webSocket.StopFolderBrowser(this.eventCallback);
        },

        eventCallback(eventData) {
            const eventStr = eventData.toString();
            if (!eventStr.includes('HeartBeat'))
                console.log(`FlowV2 folderGrid eventCallback eventData:${eventData}`);

            const data = JSON.parse(eventStr);

            this.InvokeEvent(data);
        },
        async InvokeEvent(data) {
            switch (data.EventType) {
                case "AddFolder":
                case "AddProject":
                case "AddClass":
                case "AddMethod":
                    this.rememberFolderRow(data.Content);
                    if (!this.gridApi.getRowNode(data.Content.ID))
                        this.gridApi.applyTransaction({ add: [data.Content] });
                    break;
                case "UpdateMethod":
                    if (data.Content?.Name) {
                        this.updateMethodName(data.Content.ID, data.Content.Name);
                        this.$emit('rename-method', data.Content.ID, data.Content.Name);
                    }
                    this.$emit('update-method', data.Content.ID);
                    break;

                case "DeleteMethod":
                case "DeleteClass":
                case "DeleteProject":
                case "DeleteFolder":
                    const node = this.gridApi.getRowNode(data.Content.ID);
                    if (node) {
                        let list = [];
                        this.removeNodeWithChildren(node, list);
                        console.log(`Removing: ${JSON.stringify(list)}`);
                        this.gridApi.applyTransaction({ remove: list });
                        this.forgetFolderRows(list);
                    }
                    else
                        this.forgetFolderRows([{ ID: data.Content.ID }]);
                    break;
                case "MoveFolder":
                case "MoveProject":
                case "MoveClass":
                case "MoveMethod": {
                    const rows = Array.isArray(data.Content?.Rows) ? data.Content.Rows : [];
                    const updates = rows.map(row => ({ oldId: row.OldID || row.ID, row }));
                    this.applyMovedRows(updates);
                    this.notifyMoved(updates);
                    break;
                }
            }
        },

        folderGetDataPath(data) {
            if (data.Path)
                return [...data.Path.split('/'), data.Name];
            else
                return [data.Name];
        },
        getRowId(params) {
            return params.data.ID;
        },
        getRowStyle(params) {
            const rowId = params.node?.data?.ID;
            if (!rowId)
                return undefined;

            const projectConfig =
                this.editorSurface === 'config' && this.configScope === 'project';

            if (projectConfig) {
                if (this.projectId && rowId === this.projectId)
                    // Distinct from method selection blue (#afcfef)
                    return { background: '#d7f0e3' };
                if (this.FlowID && rowId === this.FlowID)
                    // Mute the open method while project settings are in focus
                    return { background: '#eef1f4' };
                return undefined;
            }

            if (this.FlowID && rowId === this.FlowID)
                return { background: '#afcfef' };
            return undefined;
        },
        onCellClicked(params) {
            const clickedMenuColumn = params.column?.getColId?.() === 'Menu';
            const clickedMenuControl = !!params.event?.target?.closest?.('.q-btn, .q-menu');
            if (clickedMenuColumn || clickedMenuControl)
                return;

            if (params.node.hasChildren()) {
                // Toggle expansion (or use params.node.setExpanded(true) to always expand)
                params.node.setExpanded(!params.node.expanded);
            }
            else if (params.node.data.Type == 'APIHeader' || params.node.data.Type == 'FlowHeader') {
                this.emitOpenFlowFromRow(params.data, params.node);
            }
        },

        openInputDialog(title, saveFunc) {
            let value = '';
            this.$refs.genericDialog.showDialog(title, 500, (dlg) =>
                <QInput dense
                    outlined
                    hide-bottom-space
                    autofocus
                    placeholder={title}
                    autocomplete="off"
                    modelValue={value}
                    onKeyup={(k) => { k.key == 'Enter' ? dlg.Okay() : null; }}
                    onUpdate:modelValue={(v) => value = v}
                />
            , () => saveFunc(value));
        },
        openAddProjectDialog(path, parentfolder, parentData) {
            // Reactive on this so QSelect re-renders (local lets do not update controlled Quasar selects)
            this.newProjectName = '';
            this.newProjectChannelType = CHANNEL_TYPE_CALL;
            const channels = this.channelList?.length ? this.channelList : builtinChannelFallbackList();
            this.$refs.genericDialog.showDialog('Add Project', 520, (dlg) =>
                <div class="q-gutter-md">
                    <QInput dense
                        outlined
                        hide-bottom-space
                        autofocus
                        label="Name"
                        autocomplete="off"
                        modelValue={this.newProjectName}
                        onKeyup={(k) => { k.key == 'Enter' ? dlg.Okay() : null; }}
                        onUpdate:modelValue={(v) => { this.newProjectName = v; }}
                    />
                    <QSelect dense
                        outlined
                        hide-bottom-space
                        emit-value
                        map-options
                        popup-content-class="q-select-max-popup-height"
                        label="Channel"
                        options={channels.map(c => ({
                            label: c.Description || c.ChannelType,
                            value: c.ChannelType,
                            icon: c.Icon || null,
                        }))}
                        modelValue={this.newProjectChannelType}
                        onUpdate:modelValue={(v) => { this.newProjectChannelType = v; }}
                    >
                        {{
                            selected: () => {
                                const ch = channels.find(c => c.ChannelType === this.newProjectChannelType);
                                return (
                                    <div class="row items-center no-wrap">
                                        {ch?.Icon
                                            ? <QIcon name={ch.Icon} size="20px" class="q-mr-sm" />
                                            : null}
                                        <span>{ch?.Description || ch?.ChannelType || ''}</span>
                                    </div>
                                );
                            },
                            option: (scope) => (
                                <QItem key={scope.index} {...scope.itemProps}>
                                    {scope.opt?.icon
                                        ? <QItemSection avatar>
                                            <QIcon name={scope.opt.icon} />
                                          </QItemSection>
                                        : null}
                                    <QItemSection>
                                        <QItemLabel>{scope.opt.label}</QItemLabel>
                                    </QItemSection>
                                </QItem>
                            ),
                        }}
                    </QSelect>
                </div>
            , () => this.addProject(path, parentfolder, this.newProjectName, parentData, this.newProjectChannelType));
        },
        methodDialogCanSave() {
            const name = (this.newMethodName || '').trim();
            if (!name)
                return false;
            if (this.startFromExample && !this.exampleRecipeId)
                return false;
            return true;
        },
        filteredExampleRecipes() {
            const filter = (this.exampleRecipeFilter || '').trim().toLowerCase();
            const rows = this.exampleRecipes || [];
            if (!filter)
                return rows;
            return rows.filter(row => ((row.title || '') + ' ' + (row.task || '')).toLowerCase().includes(filter));
        },
        async loadExampleRecipes(channel, kind) {
            this.exampleRecipesLoading = true;
            this.exampleRecipes = [];
            this.exampleRecipeId = '';
            this.exampleRecipesEmpty = false;
            try {
                const raw = await webSocket.AskAiListRecipes(channel, kind);
                const result = typeof raw === 'string' ? JSON.parse(raw) : raw;
                const rows = Array.isArray(result?.recipes) ? result.recipes.filter(row => row && row.id) : [];
                this.exampleRecipes = rows;
                this.exampleRecipesEmpty = rows.length === 0;
                if (!rows.length)
                    this.startFromExample = false;
            }
            catch (err) {
                this.exampleRecipes = [];
                this.exampleRecipesEmpty = true;
                this.startFromExample = false;
            }
            finally {
                this.exampleRecipesLoading = false;
            }
        },
        openAddMethodDialog(title, kind, path, parentfolder, parentData) {
            this.newMethodName = '';
            this.startFromExample = false;
            this.exampleRecipes = [];
            this.exampleRecipeId = '';
            this.exampleRecipeFilter = '';
            this.exampleRecipesLoading = false;
            this.exampleRecipesEmpty = false;
            const node = this.gridApi?.getRowNode?.(parentData?.ID);
            const channel = ancestorProjectChannelType(node) || (kind === 'API' ? 'API' : CHANNEL_TYPE_CALL);
            const showExamples = !!this.askAiEnabled && !!channel;
            if (showExamples)
                this.loadExampleRecipes(channel, kind);
            this.$refs.genericDialog.showDialog(title, 560, (dlg) => {
                const recipes = this.filteredExampleRecipes();
                return (
                    <div class="q-gutter-md">
                        <QInput dense
                            outlined
                            hide-bottom-space
                            autofocus
                            label="Name"
                            autocomplete="off"
                            modelValue={this.newMethodName}
                            onKeyup={(k) => { k.key == 'Enter' ? dlg.Okay() : null; }}
                            onUpdate:modelValue={(v) => { this.newMethodName = v; }}
                        />
                        {showExamples && !this.exampleRecipesEmpty && (this.exampleRecipesLoading || this.exampleRecipes.length) ? (
                            <div>
                                <QCheckbox
                                    dense
                                    label="Start from an example"
                                    modelValue={this.startFromExample}
                                    onUpdate:modelValue={(v) => { this.startFromExample = !!v; }}
                                />
                                {this.startFromExample ? (
                                    <div class="q-mt-sm">
                                        <QInput dense outlined hide-bottom-space placeholder="Search examples" modelValue={this.exampleRecipeFilter} onUpdate:modelValue={(v) => { this.exampleRecipeFilter = v; }} />
                                        <QList dense bordered class="q-mt-sm rounded-borders" style={{ maxHeight: '220px', overflow: 'auto' }}>
                                            {this.exampleRecipesLoading ? <QItem><QItemSection><QItemLabel>Loading examples…</QItemLabel></QItemSection></QItem> : null}
                                            {!this.exampleRecipesLoading && !recipes.length ? <QItem><QItemSection><QItemLabel>No matching examples</QItemLabel></QItemSection></QItem> : null}
                                            {recipes.map(row => (
                                                <QItem
                                                    key={row.id}
                                                    clickable
                                                    active={this.exampleRecipeId === row.id}
                                                    onClick={() => { this.exampleRecipeId = row.id; }}
                                                >
                                                    <QItemSection>
                                                        <QItemLabel>{row.title || 'Example'}</QItemLabel>
                                                        {row.task ? <QItemLabel caption>{row.task}</QItemLabel> : null}
                                                    </QItemSection>
                                                </QItem>
                                            ))}
                                        </QList>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                );
            }, () => {
                const name = (this.newMethodName || '').trim();
                const recipeId = this.startFromExample ? this.exampleRecipeId : '';
                if (!name)
                    return;
                if (kind === 'API')
                    this.addAPI(path, parentfolder, name, parentData, recipeId);
                else
                    this.addFlow(path, parentfolder, name, parentData, recipeId);
            });
            this.$refs.genericDialog.canSave = () => this.methodDialogCanSave();
        },
        openConfirmDialog(title, body, saveFunc) {
            this.$refs.genericDialog.showDialog(title, 500, () => body, saveFunc);
        },
        
        async addSubFolder(path, parentfolder, name, parentData) {
            //debugger;
            const folder = path ? (path + '/' + parentfolder) : parentfolder;
            const newRow = {
                ID: folder ? (folder + '/' + name) : name,
                Type: 'Folder',
                Name: name,
                Path: folder,
                children: [],
            };
            this.rememberFolderRow(newRow);
            this.gridApi.applyTransaction({ add: [newRow] });
            this.expandParentRowAfterAdd(parentData);
            this.revealRowInGrid(newRow.ID);

            let fullname = newRow.Path ? (newRow.Path + '/' + name) : name;
            let res = await utils.api.get(`Apps/FlowV2/CreateFolder?Name=${fullname}`);
            if (!res.Success)
                alert(`Failed to create folder: ${res.Reason}`);
        },
        async addProject(path, parentfolder, name, parentData, channelType) {
            const newRow = {
                ID: utils.generateUUID(),
                Type: 'Project',
                Name: name,
                Path: path ? (path + '/' + parentfolder) : parentfolder,
                ChannelType: channelType || CHANNEL_TYPE_CALL,
            };
            this.rememberFolderRow(newRow);
            this.gridApi.applyTransaction({ add: [newRow] });
            this.expandParentRowAfterAdd(parentData);
            this.revealRowInGrid(newRow.ID);

            let res = await utils.api.request('POST', 'Apps/FlowV2/SaveProject?IsNew=true', newRow);
            if (!res.Success)
                alert(`Failed to create project: ${res.Reason}`);
        },
        async addClass(path, parentfolder, name, parentData) {
            if ((name || '').trim() == 'Default') {
                alert('Default is a reserved class name.');
                return;
            }
            const newRow = {
                ID: utils.generateUUID(),
                Type: 'Class',
                Name: name,
                Path: path ? (path + '/' + parentfolder) : parentfolder,
            };
            this.rememberFolderRow(newRow);
            this.gridApi.applyTransaction({ add: [newRow] });
            this.expandParentRowAfterAdd(parentData);
            this.revealRowInGrid(newRow.ID);

            let res = await utils.api.request('POST', 'Apps/FlowV2/SaveClass?IsNew=true', newRow);
            if (!res.Success)
                alert(`Failed to create class: ${res.Reason}`);
        },
        async addFlow(path, parentfolder, name, parentData, recipeId) {
            const uri = await resolveLogicalUri(utils.api, {
                projectPath: path || '',
                projectName: '',
                className: parentfolder,
                methodName: name,
            });
            const newRow = {
                ID: utils.generateUUID(),
                Type: 'FlowHeader',
                Name: name,
                Path: path ? (path + '/' + parentfolder) : parentfolder,
                Parameters: [],
                ReturnType: 'string',
                Configuration: {
                    Method: 'GET',
                    URITemplate: uri,
                    Public: false,
                    WebAccessible: false,
                },
            };
            this.rememberFolderRow(newRow);
            this.gridApi.applyTransaction({ add: [newRow] });
            this.expandParentRowAfterAdd(parentData);
            this.revealRowInGrid(newRow.ID);

            let res = await utils.api.request('POST', 'Apps/FlowV2/SaveMethodHeader?Type=FlowHeader&IsNew=true', newRow);
            if (!res.Success)
                alert(`Failed to create flowchart: ${res.Reason}`);
            else {
                this.$nextTick(() => this.emitOpenFlowFromRow(newRow, undefined, true, recipeId));
            }
        },
        async addAPI(path, parentfolder, name, parentData, recipeId) {
            const uri = await resolveLogicalUri(utils.api, {
                projectPath: path || '',
                projectName: '',
                className: parentfolder,
                methodName: name,
            });
            const newRow = {
                ID: utils.generateUUID(),
                Type: 'APIHeader',
                Name: name,
                Path: path ? (path + '/' + parentfolder) : parentfolder,
                Parameters: [],
                ReturnType: 'string',
                Configuration: {
                    Method: 'GET',
                    URITemplate: uri,
                    Public: false,
                    WebAccessible: false,
                },
            };
            this.rememberFolderRow(newRow);
            this.gridApi.applyTransaction({ add: [newRow] });
            this.expandParentRowAfterAdd(parentData);
            this.revealRowInGrid(newRow.ID);

            let res = await utils.api.request('POST', 'Apps/FlowV2/SaveMethodHeader?Type=APIHeader&IsNew=true', newRow);
            if (!res.Success)
                alert(`Failed to create API: ${res.Reason}`);
            else {
                this.$nextTick(() => this.emitOpenFlowFromRow(newRow, undefined, false, recipeId));
            }
        },
        async deleteMethod(data) {
            let res = await utils.api.request('DELETE', `Apps/FlowV2/DeleteMethod?ID=${data.ID}&Type=${data.Type}`);
            if (res.Success) {
                // Remove from grid
                this.gridApi.applyTransaction({ remove: [data] });
                this.forgetFolderRows([data]);
            }
        },
        removeNodeWithChildren(node, list) {
            list.push(node.data);
            if (node.allLeafChildren && Array.isArray(node.allLeafChildren))
                for (let child of node.allLeafChildren) {
                    list.push(child.data);
                    if (child.allChildrenCount)
                        this.removeNodeWithChildren(child, list);
                }
        },
        async deleteClass(data) {
            let res = await utils.api.request('DELETE', `Apps/FlowV2/DeleteClass?ID=${data.ID}`);
            if (res.Success) {
                // Remove from grid
                let node = this.gridApi.getRowNode(data.ID);
                if (node) {
                    let list = [];
                    this.removeNodeWithChildren(node, list);
                    console.log(`Removing: ${JSON.stringify(list)}`);
                    this.gridApi.applyTransaction({ remove: list });
                    this.forgetFolderRows(list);
                }
            }
        },
        async deleteProject(data) {
            let res = await utils.api.request('DELETE', `Apps/FlowV2/DeleteProject?ID=${data.ID}`);
            if (res.Success) {
                // Remove from grid
                let node = this.gridApi.getRowNode(data.ID);
                if (node) {
                    let list = [];
                    this.removeNodeWithChildren(node, list);
                    console.log(`Removing: ${JSON.stringify(list)}`);
                    this.gridApi.applyTransaction({ remove: list });
                    this.forgetFolderRows(list);
                }
            }
        },
        async deleteFolder(data) {
            let res = await utils.api.request('DELETE', `Apps/FlowV2/DeleteFolder?FullName=${data.ID}`);
            if (res.Success) {
                // Remove from grid
                let node = this.gridApi.getRowNode(data.ID);
                if (node) {
                    let list = [];
                    this.removeNodeWithChildren(node, list);
                    console.log(`Removing: ${JSON.stringify(list)}`);
                    this.gridApi.applyTransaction({ remove: list });
                    this.forgetFolderRows(list);
                }
            }
            else
                alert(res.Reason);
        },

        async openSettings(data) {
            this.emitOpenConfig(data);
        },
        async saveSettings(data) {
            const res = await utils.api.request('POST', `Apps/FlowV2/SaveMethodHeader?Type=${data.Type}`, data);
            if (!res.Success)
                alert(res.Reason);
            else
                this.$emit('update-method', data.ID, data);
        },

        async projectSettings(data) {
            this.emitOpenConfig(data);
        },
        async saveProjectSettings(data) {
            const res = await utils.api.request('POST', `Apps/FlowV2/SaveProject`, data);
            if (!res.Success)
                alert(res.Reason);
        },

        resolveProjectId(rowData) {
            let node = this.gridApi?.getRowNode?.(rowData.ID);
            let walk = node;
            while (walk?.data && walk.data.Type != 'Project' && walk.parent)
                walk = walk.parent;
            if (walk?.data?.Type === 'Project')
                return walk.data.ID;
            return undefined;
        },
        emitOpenConfig(data) {
            const projectId = data.Type === 'Project' ? data.ID : this.resolveProjectId(data);
            this.$emit('open-config', {
                ...data,
                ProjectID: projectId,
            });
        },

        folderEvent(event, data) {
            if (event !== 'open-config' && isFolderRowDebugLocked(data, this.gridApi?.getRowNode?.(data.ID)))
                return;
            switch (event) {
                case 'open-config':
                    this.emitOpenConfig(data);
                    break;

                case 'add-folder': this.openInputDialog('Add Folder', (value) => this.addSubFolder(data.Path, data.Name, value, data)); break;
                case 'add-project': this.openAddProjectDialog(data.Path, data.Name, data); break;
                case 'add-class': this.openInputDialog('Add Class', (value) => this.addClass(data.Path, data.Name, value, data)); break;
                case 'add-flow': this.openAddMethodDialog('Add Flowchart', 'Flow', data.Path, data.Name, data); break;
                case 'add-api': this.openAddMethodDialog('Add API', 'API', data.Path, data.Name, data); break;

                case 'delete-folder':
                    this.openConfirmDialog(
                        `Confirm delete folder`,
                        `Are you sure you want to delete the folder named ${data.Name} with all its subfolders and projects (with all the entities within)?`,
                        () => this.deleteFolder(data));
                    break;
                case 'delete-project':
                    this.openConfirmDialog(
                        `Confirm delete project`,
                        `Are you sure you want to delete the project named ${data.Name} with all its flowcharts and APIs?`,
                        () => this.deleteProject(data));
                    break;
                case 'delete-class':
                    this.openConfirmDialog(
                        `Confirm delete class`,
                        `Are you sure you want to delete the class named ${data.Name} with all its flowcharts and APIs?`,
                        () => this.deleteClass(data));
                    break;

                case 'delete':
                    let type = data.Type == 'FlowHeader' ? 'flowchart' : 'API';
                    this.openConfirmDialog(
                        `Confirm delete ${type}`,
                        `Are you sure you want to delete the ${type} named ${data.Name}?`,
                        () => this.deleteMethod(data));
                    break;
                case 'save-recipe':
                    if (data.Type != 'FlowHeader' && data.Type != 'APIHeader')
                        break;
                    if (data.ID !== this.FlowID)
                        this.emitOpenFlowFromRow(data);
                    this.$emit('save-recipe', data);
                    break;
            }
        },
    },
    render() {
        let content = [
            <FlowV2ListToolbar
                modelValue={this.folderSearchText}
                onUpdate:modelValue={(v) => { this.folderSearchText = v; }}
            >
                <QBtn flat round dense icon="mdi-plus" color="blue-grey-8">
                    <QTooltip>Add</QTooltip>
                    <QMenu anchor="bottom end" self="top end" auto-close>
                        <QList dense>
                            <QItem clickable onClick={() => this.openInputDialog('Add Folder', (name) => this.addSubFolder('', '', name, null))}>
                                <QItemSection><QItemLabel>Add Folder</QItemLabel></QItemSection>
                            </QItem>
                            <QItem clickable onClick={() => this.openAddProjectDialog('', '', null)}>
                                <QItemSection><QItemLabel>Add Project</QItemLabel></QItemSection>
                            </QItem>
                        </QList>
                    </QMenu>
                </QBtn>
            </FlowV2ListToolbar>,
            <AgGridVue
                ref="folderAgGrid"
                style="flex-grow: 1; display: flex; flex-direction: column;"
                headerHeight="0"
                treeData={true}
                autoGroupColumnDef={this.autoGroupColumnDef}
                defaultColDef={this.defaultColDef}
                columnDefs={this.folderColumnDefs}
                rowData={this.folderData}
                getDataPath={this.folderGetDataPath}
                onCellClicked={this.onCellClicked}
                components={this.gridComponents}
                xxrowSelection={{ mode: 'singleRow', checkboxes: false, enableClickSelection: true }}
                cellSelection={false}
                quickFilterText={this.folderSearchText}
                rowDragManaged={false}
                suppressMoveWhenRowDragging={true}
                getRowId={this.getRowId}
                getRowStyle={this.getRowStyle}
                getRowClass={this.getRowClass}
                onGridReady={this.gridReady}
                onFirstDataRendered={this.onFirstDataRendered}
                onRowGroupOpened={this.onFolderRowGroupOpened}
            >
            </AgGridVue>,
            <GenericDialog ref="genericDialog"></GenericDialog>,
        ];

        return (
            <div
                class="flowv2-folder-grid"
                onDragover={this.onTreeDragOver}
                onDrop={this.onTreeDrop}
            >
                {content[0]}
                <div
                    class={{ 'flowv2-folder-root-drop': true, 'is-active': this.dropTargetId === '__root__' }}
                    title="Drop a folder or project here to move it to the top level"
                    onDragover={this.onRootDragOver}
                    onDrop={this.onRootDrop}
                />
                {content.slice(1)}
                <div ref="dropHint" class="flowv2-folder-drop-hint" />
            </div>
        );
    }
});